import {type Stripe, loadStripe} from '@stripe/stripe-js';
import {maybeFetchWP} from '../../../../../frontend/@shared/ts/maybe';
import {type IResponse} from '../../../../../frontend/@type/response';
import {z} from '@peachpay/checkout-blocks';

declare global {
	interface Window {
		/**
		 * Stripe throws an error after about 10 instances of the stripe object
		 * are created. We store the first instance here so it is shared.
		 */
		peachpay_stripe?: Promise<Stripe | null>;
	}
}

const stripePromise = async (public_key: string, stripeAccount: string) =>
	new Promise<Stripe | string | null>(resolve => {
		try {
			if (window.peachpay_stripe) {
				resolve(window.peachpay_stripe);
				return;
			}

			window.peachpay_stripe = loadStripe(public_key, {
				locale: 'auto',
				stripeAccount,
			});

			resolve(window.peachpay_stripe);
		} catch (error) {
			resolve((error as Error).message);
		}
	});

type CreateSetupIntentParams = {
	setup_intent_url: string;
	setup_intent_nonce: string;
	payment_method_type: string;
	payment_method_ID?: string;
};

const SetupIntentFetchResultSchema = z.object({
	setup_intent_details: z.object({
		client_secret: z.string(),
	}),
});

async function createSetupIntent(params: CreateSetupIntentParams) {
	const formData = new FormData();
	formData.append('security', params.setup_intent_nonce);
	formData.append('payment_method_type', params.payment_method_type);

	if (params.payment_method_ID) {
		formData.append('payment_method', params.payment_method_ID);
	}

	const {error, result} = await maybeFetchWP<IResponse<unknown>>(params.setup_intent_url, {
		method: 'POST',
		body: formData,
	});

	if (error || !result?.data) {
		return null;
	}

	const setupIntentResult = SetupIntentFetchResultSchema.safeParse(result.data);

	if (!setupIntentResult.success) {
		return null;
	}

	return setupIntentResult.data;
}

function parsePaymentData(encodedStringData: string) {
	const encodedPaymentData = z.object({
		data: z.object({client_secret: z.string()}),
		intermediate_url: z.string(),
		success_url: z.string(),
		transaction_id: z.string(),
	});

	let encodedData: unknown;
	try {
		encodedData = JSON.parse(atob(decodeURIComponent(encodedStringData)));
	} catch (error) {
		console.error(error);
		return null;
	}

	const parsedData = encodedPaymentData.safeParse(encodedData);

	if (!parsedData.success) {
		console.error(parsedData.error);
		return null;
	}

	return parsedData.data;
}

export {stripePromise as loadStripe, createSetupIntent, parsePaymentData};
